import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import Styles from "./FeatureCards.module.css";

/**
 * FeatureCards — a reusable card grid. One primitive covers the "audience
 * split" (2 wide cards, no image) and the "ecosystem" (3 cards with image +
 * tag + brand line + bullet list + link) sections of the Military page.
 *
 * Every card element is optional, in this order: image (card-top), then a
 * kicker `label` OR an accent `tag`, an optional accent `brand` display line
 * above the `title`, a rich-text `description` (paragraphs and/or a bullet
 * list), and an optional card CTA gated on both label and link.
 *
 * `columns` caps the grid: "Default" floors cards at 340px (3-up ceiling),
 * "Wide" at 420px (2-up). Below that both degrade to one column.
 *
 * @typedef {import("@prismicio/client").Content.FeatureCardsSlice} FeatureCardsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FeatureCardsSlice>} FeatureCardsProps
 * @param {FeatureCardsProps}
 */
const FeatureCards = ({ slice: { primary, items } }) => {
	const columns = (primary?.columns || "").toLowerCase().includes("wide")
		? "wide"
		: "default";

	const hasHeader =
		isFilled.keyText(primary?.eyebrow) ||
		isFilled.richText(primary?.heading) ||
		isFilled.richText(primary?.intro);

	return (
		<section
			className={Styles.wrapper}
			data-theme={primary?.dark_theme ? "dark" : "light"}>
			<div className={Styles.container} data-contain="true">
				{hasHeader && (
					<header className={Styles.header}>
						{primary?.eyebrow && (
							<p className={Styles.eyebrow}>{primary?.eyebrow}</p>
						)}
						{isFilled.richText(primary?.heading) && (
							<div className={Styles.heading}>
								<PrismicRichText field={primary?.heading} />
							</div>
						)}
						{isFilled.richText(primary?.intro) && (
							<div className={Styles.intro}>
								<PrismicRichText field={primary?.intro} />
							</div>
						)}
					</header>
				)}

				<ul className={Styles.grid} data-columns={columns}>
					{items?.map((item, index) => {
						const hasCta =
							item?.cta_label && isFilled.link(item?.cta_link);
						return (
							<li className={Styles.card} key={index}>
								<span className={Styles.cardAccent} aria-hidden="true"></span>
								{isFilled.image(item?.image) && (
									<div className={Styles.media}>
										<PrismicNextImage
											field={item?.image}
											fill
											sizes="(max-width: 768px) 100vw, 33vw"
											style={{ objectFit: "cover" }}
										/>
									</div>
								)}
								<div className={Styles.body}>
									{item?.label && (
										<p className={Styles.label}>{item?.label}</p>
									)}
									{item?.tag && (
										<p className={Styles.cardTag}>
											<span aria-hidden="true"></span>
											{item?.tag}
										</p>
									)}
									<h3 className={Styles.title}>
										{item?.brand && (
											<span className={Styles.brand}>{item?.brand}</span>
										)}
										{item?.title}
									</h3>
									{isFilled.richText(item?.description) && (
										<div className={Styles.description}>
											<PrismicRichText field={item?.description} />
										</div>
									)}
									{hasCta && (
										<PrismicNextLink
											field={item?.cta_link}
											className={Styles.cardCta}>
											{item?.cta_label}
										</PrismicNextLink>
									)}
								</div>
							</li>
						);
					})}
				</ul>
			</div>
		</section>
	);
};

export default FeatureCards;
